import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { Course } from '@shapeable/ibj-types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, formatDateString } from '@shapeable/utils';
import fecha from 'fecha';
import { Button, OutlineEntityNumber, useLang } from '@shapeable/ui';
import { KeyIssueIcon, LayoutIcon } from '@shapeable/icons';
import { sprintf } from 'sprintf-js';
import { Card, Content, EntityImage, EntityCellHeader, IconLabel, useIcons, useEntityDefinition, EntityFieldCountLabel } from '@shapeable/core';

const cls = classNames('course-card');

// -------- Types -------->

// -------- Props -------->

export type CourseCardProps = Classable & HasChildren & { 
  entity?: Course;
};

export const CourseCardDefaultProps: Omit<CourseCardProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const CardStyles = breakpoints({
  base: css`
    display: flex;
    margin-bottom: ${theme.UNIT(4)};
  `,
});

const ContentStyles = breakpoints({
  base: css`
    flex-grow: 1;
    padding-top: ${theme.UNIT(3)};
    padding-bottom: ${theme.UNIT(3)};
    margin-top: ${theme.UNIT(3)};
    ${theme.H_DOT_BG()};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    font-family: ${theme.FONT('sans')};
    font-size: ${theme.FONT_SIZE(21)};
    font-weight: 500;
    line-height: 1.25em;
    margin-bottom: 0;
    color: ${theme.COLOR('dark')};
    background: none;
    margin-top: 0;
    margin-bottom: ${theme.UNIT(2)};


    &:hover {
      color: ${theme.COLOR('link-hover')};
    }
  `,
});

const ImageStyles = breakpoints({
  base: css`
  `,
});

const LabelStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(2)};
  `,
});

const HeaderStyles = breakpoints({
  base: css`
  `,
});

const IconLabelStyles = breakpoints({
  base: css`
    ${LabelStyles};
  `,
});

const ButtonStyles = breakpoints({
  base: css`
    
  `,
});

const ButtonsStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    padding-top: ${theme.UNIT(2)};
  `,
});

const OutlineNumberStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('black')};
  `,
});

// -------- Components -------->

const My = {
  Card: styled(Card)`${CardStyles}`,
  Image: styled(EntityImage)`${ImageStyles}`,
  Content: styled(Content)`${ContentStyles}`,
  Header: styled(EntityCellHeader)`${HeaderStyles}`,
  Title: styled.span`${TitleStyles}`,
  CountLabel: styled(EntityFieldCountLabel)`${LabelStyles}`,
  IconLabel: styled(IconLabel)`${IconLabelStyles}`,
  OutlineNumber: styled(OutlineEntityNumber)`${OutlineNumberStyles}`,

  Buttons: styled.div`${ButtonsStyles}`,
    DetailsButton: styled(Button)`${ButtonStyles}`,
    RegisterButton: styled(Button)`${ButtonStyles}`,
};

export const CourseCard: Shapeable.FC<CourseCardProps> = (props) => {
  const { className, children, entity } = props;

  const { registrationUrl, startDate, lessons = [], program, isCurrentUserParticipating, outlineNumber } = entity || {};

  const Icons = useIcons();

  const t = useLang();

  const definition = useEntityDefinition(entity);
    

  const onRegisterButtonClick: React.MouseEventHandler = (event) => {
    if (!registrationUrl) return;

    event.preventDefault();
    event.stopPropagation();
    window.open(registrationUrl, '_blank');
  };


  return (
    <My.Card
      {...props}
      // hspace={hspace}
      space={4}
      titleMargin="none"
      to={definition.to(entity?.slug || '')}
      className={className}
      image={<My.Image entity={entity as Course} />}
      >
        <My.Header entity={entity as Course} />
        <My.Title>{entity?.name}</My.Title>
        {
          !!outlineNumber &&
          <My.OutlineNumber entity={entity as Course} sizes={ {1: 2.5, 2: 2.5, 3: 2.5, 4: 2.5, 5: 2.5} } colors={{ text: 'black' }} />
        }
        {
          !!program &&
          <My.IconLabel icon={<LayoutIcon />} >
            {t("Program:")} {program?.name}
          </My.IconLabel>
        }
        {
          !!startDate &&
          <My.IconLabel icon={<Icons.BriefcaseCircle />} >
            {t("Starting:")} {formatDateString(startDate, 'DD MMM YYYY')}
          </My.IconLabel>
        }
        {
          !!lessons.length &&
          <My.IconLabel icon={<KeyIssueIcon />} >
            {sprintf(lessons.length === 1 ? t("%s Module") : t("%s Modules"), lessons.length)}
          </My.IconLabel>
        }

        <My.Buttons>
        <My.DetailsButton>{t('Enter Course')}</My.DetailsButton>
        {
          !isCurrentUserParticipating && !!registrationUrl &&
          <My.RegisterButton onClick={onRegisterButtonClick}>{t("Register")}</My.RegisterButton>
        }
        </My.Buttons>
        

        {/* {
          description && description.plain && 
          <My.Content tiny html={description.plain} />
        } */}
        
        {children}
    </My.Card>
  )
};

CourseCard.cls = cls;